/**
 * Deriving a host's presentation state from its heartbeat, and formatting the
 * idle-stop countdown. Pure so the logic is unit-tested without React.
 *
 * A host is "unreachable" when its heartbeat is older than 45 s (spec,
 * "Heartbeat") — three missed 15 s beats — regardless of the `status` field it
 * last wrote, because a VM that was powered off cannot update `status` to
 * `stopped` itself.
 *
 * The one state in which `status` outranks a live heartbeat is a stop in progress:
 * a host that has written `stopping` goes on beating until the halt actually takes
 * it, so those beats say "answering", not "available", and the card must not read
 * them as online. That deference is bounded by {@link STOP_GRACE_MS} — a stop that
 * never happens has to stop being believed.
 */
import type { HostDoc, HostInstance } from '@finvari/deployer';

export const HEARTBEAT_STALE_MS = 45_000;

/**
 * A beat is "late" past this — two missed 15 s beats — and worth saying so on an
 * online card. Deliberately below {@link HEARTBEAT_STALE_MS}: past that cutoff the
 * host is not presented as online at all, so this is the only window in which a
 * heartbeat age is both visible and worth reading. An online host that is beating
 * on schedule says nothing about it, because a number climbing toward the cutoff
 * reads as a countdown to something bad when nothing is wrong.
 */
export const HEARTBEAT_LATE_MS = 30_000;

/**
 * How long a host that is still beating is believed to be in the middle of going
 * down, once it has written `stopping`.
 *
 * A stop is not instant and the host keeps beating through it: `shutdown -h now`
 * returns immediately, and the controller is only killed when systemd gets to it.
 * So "says stopping, still beating" is the normal state for the first part of
 * every shutdown, and the card has to hold "Stopping" rather than read the beats
 * as "online" — a host mid-halt must not be offered Shutdown or anything else.
 *
 * Three minutes because the two ways a stop is bounded — systemd's default 90 s
 * unit stop timeout, and Compute's 90 s ACPI window on `instances.stop` — both
 * finish well inside it. Past that the host has demonstrably not gone down (the
 * stop failed, or it came back up), and the beats are better evidence than the
 * mark, so the card trusts them and says the stop did not take.
 */
export const STOP_GRACE_MS = 3 * 60_000;

/**
 * `loading` is distinct from `unknown` on purpose. `unknown` is an answer — the
 * host has never written a document — and the UI acts on it by offering Start.
 * `loading` is the absence of an answer, and the only correct response to it is
 * to offer nothing and claim nothing.
 */
export type HostPresentation = 'loading' | 'online' | 'starting' | 'stopping' | 'offline' | 'unknown';

/**
 * How a presentation reads as a status colour and a one-word label — shared by the
 * host card's status chip and the nav-bar tab dot so the two never disagree about
 * what "online" looks like.
 */
export const PRESENTATION_COLOR: Record<HostPresentation, 'success' | 'warning' | 'default' | 'error'> = {
    loading: 'default',
    online: 'success',
    starting: 'warning',
    stopping: 'warning',
    offline: 'default',
    unknown: 'default',
};

export const PRESENTATION_LABEL: Record<HostPresentation, string> = {
    loading: 'Checking…',
    online: 'Online',
    starting: 'Starting',
    stopping: 'Stopping',
    offline: 'Offline',
    unknown: 'No record yet',
};

export interface HostView {
    presentation: HostPresentation;
    heartbeatAgeMs?: number;
    /** ms since `status` was last set by a lifecycle transition (starting/stopping). */
    statusAgeMs?: number;
    /** ms until the idle stop, if armed and in the future. */
    idleStopInMs?: number;
    held: boolean;
    /**
     * A stop was asked for more than {@link STOP_GRACE_MS} ago and the host is still
     * beating. The presentation is `online` — that is what the evidence says — but
     * the operator asked for the opposite and needs telling, because nothing else on
     * the card distinguishes this from a host nobody touched.
     */
    stopOverdue?: boolean;
}

/**
 * A boot that has run this many times the expected duration without a heartbeat
 * is shown as overdue, with the remediation. Two, not one, because boot time
 * varies with image updates and disk warm-up and a false alarm sends an operator
 * to the console for nothing.
 */
export const BOOT_OVERDUE_FACTOR = 2;

/** Whether a host shown as `starting` has been at it longer than it should. */
export function bootOverdue(view: HostView, expectedBootSeconds: number): boolean {
    return (
        view.presentation === 'starting' &&
        view.statusAgeMs !== undefined &&
        view.statusAgeMs > expectedBootSeconds * 1000 * BOOT_OVERDUE_FACTOR
    );
}

/** Milliseconds from a stored timestamp-ish value to `now`. */
function ageMs(ts: { toMillis?: () => number } | undefined, now: number): number | undefined {
    if (!ts?.toMillis) return undefined;
    return now - ts.toMillis();
}

/**
 * `loading` is checked before the document, because "no document yet" and "no
 * snapshot yet" arrive here as the same `undefined` (see `Loadable` in `hooks`).
 */
export function hostView(doc: HostDoc | undefined, now: number = Date.now(), loading = false): HostView {
    if (loading) return { presentation: 'loading', held: false };
    if (!doc) return { presentation: 'unknown', held: false };
    const heartbeatAgeMs = ageMs(doc.lastHeartbeat, now);
    const statusAgeMs = ageMs(doc.statusUpdatedAt, now);
    const stale = heartbeatAgeMs === undefined || heartbeatAgeMs > HEARTBEAT_STALE_MS;

    // Halting: it said "stopping" and is still beating, which is what the first part
    // of every shutdown looks like. Past the grace window it is not halting at all —
    // the stop failed or the host came back — and the beats are then the better
    // evidence. `?? Infinity` so a document with no `statusUpdatedAt` to measure
    // against cannot pin the card to "Stopping" with no way out of it.
    const halting = doc.status === 'stopping' && !stale;
    const stopOverdue = halting && (statusAgeMs ?? Infinity) > STOP_GRACE_MS;

    let presentation: HostPresentation;
    if (doc.status === 'starting' && stale) {
        // Booting: it said "starting" but has not beaten yet.
        presentation = 'starting';
    } else if (stale) {
        presentation = 'offline';
    } else if (halting && !stopOverdue) {
        presentation = 'stopping';
    } else {
        presentation = 'online';
    }

    const idleStopInMs = doc.idleStopDeadline?.toMillis
        ? Math.max(0, doc.idleStopDeadline.toMillis() - now)
        : undefined;

    return {
        presentation,
        heartbeatAgeMs,
        statusAgeMs,
        idleStopInMs,
        held: doc.held ?? false,
        stopOverdue,
    };
}

/** One sentence about a host, and the colour it reads in. */
export interface HostStatusLine {
    text: string;
    /** An MUI palette path, so a failure reads as one. */
    color: string;
}

/**
 * Where an operator goes when a host is not doing what it was asked. It differs by
 * surface: the host card carries the SSH command itself, while the release flow
 * deliberately does not talk about machines and so points at the page that does.
 */
export interface HostEscalation {
    /** Completes "If it does not come online, …" and "Shut it down again, or …". */
    remediation: string;
}

/**
 * What is happening on a host that is not simply online, in one sentence. An
 * online host needs no sentence — its facts (heartbeat, uptime, countdown) say
 * it — so this returns nothing for it.
 *
 * Shared by the host card and the release flow's sleeping panel, which are both
 * waiting on the same boot and must not describe it differently: the panel is what
 * an operator watches during a wake, and the card is where they end up when it does
 * not finish.
 */
export function hostStatusLine(
    view: HostView,
    vm: HostInstance | undefined,
    escalation: HostEscalation,
): HostStatusLine | undefined {
    const secondary = { color: 'text.secondary' };
    switch (view.presentation) {
        case 'online':
            // A host that was asked to stop and is still beating well past the point
            // where the halt should have taken it. Said here because nothing else on
            // an online card distinguishes it from a host nobody touched, and the
            // operator's Shutdown looks to them like it worked — the card did say
            // "Stopping" for a while before coming back.
            if (view.stopOverdue) {
                return {
                    text: `Shutdown was asked for ${formatDuration(view.statusAgeMs ?? 0)} ago and the host is still running. Shut it down again, or ${escalation.remediation}.`,
                    color: 'error',
                };
            }
            return undefined;
        // Nothing is known yet, so there is nothing to say that would not be a
        // guess; the chip already says the card is still reading.
        case 'loading':
            return undefined;
        case 'starting': {
            const expected = vm ? `about ${vm.expectedBootSeconds} s` : 'a couple of minutes';
            const soFar = view.statusAgeMs === undefined ? '' : `; ${formatDuration(view.statusAgeMs)} so far`;
            if (vm && bootOverdue(view, vm.expectedBootSeconds)) {
                return {
                    text: `Still starting after ${formatDuration(view.statusAgeMs ?? 0)} (expected ${expected}). If it does not come online, ${escalation.remediation}.`,
                    color: 'error',
                };
            }
            return { text: `Starting — usually ${expected}${soFar}. It comes online on its first heartbeat.`, ...secondary };
        }
        case 'stopping': {
            // The elapsed time is worth stating because the host is still answering
            // while it shuts down, so the card looks the same at 5 s and at 2 minutes
            // — and if it runs to the grace window the card will say the stop failed.
            const soFar = view.statusAgeMs === undefined ? '' : ` ${formatDuration(view.statusAgeMs)} so far;`;
            return { text: `Stopping —${soFar} shows offline once the last heartbeat ages out.`, ...secondary };
        }
        case 'offline':
            return {
                text:
                    view.heartbeatAgeMs === undefined
                        ? 'Offline — no heartbeat recorded yet.'
                        : `Offline — last heartbeat ${agoTextCoarse(view.heartbeatAgeMs)}.`,
                ...secondary,
            };
        case 'unknown':
            // With no VM registered there is no heartbeat to wait for; the
            // not-registered warning the caller shows is the whole story.
            return vm ? { text: 'No host record yet — it appears on the first heartbeat.', ...secondary } : undefined;
    }
}

/**
 * "just now", "5s ago", "3m 5s ago" — for a caption whose seconds are the point:
 * heartbeat age against the 15 s beat and the 45 s staleness cutoff, where the
 * operator is watching a live number to decide whether the host is answering.
 * Anything that has already happened uses {@link agoTextCoarse}.
 */
export function agoText(ms: number): string {
    if (ms < 5_000) return 'just now';
    return formatDuration(ms) + ' ago';
}

/**
 * "< 1m", "3m", "1h 2m" — a duration without seconds, for a caption that sits on
 * screen while the clock runs. A settled outcome rendered to the second
 * ("Succeeded 2m 40s ago") redraws on every tick and keeps pulling the eye back
 * to a number that stopped mattering the moment it was written.
 */
export function formatDurationCoarse(ms: number): string {
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 1) return '< 1m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

/** "< 1m ago", "2m ago", "1h 2m ago" — when something happened, to the minute. */
export function agoTextCoarse(ms: number): string {
    return formatDurationCoarse(ms) + ' ago';
}

/** "1h 12m", "3m 5s", "now" — a compact countdown for the card. */
export function formatDuration(ms: number): string {
    if (ms <= 0) return 'now';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}
